/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Skills, and who holds them.
 *
 * A queue set to "Skills, then longest idle" matches an agent who holds any
 * one of the skills it asks for. This page is where both halves of that come
 * from: the catalogue a queue picks from, and the people each skill reaches.
 *
 * The catalogue is closed rather than free text on purpose. Typing skills as
 * strings in two places is how skills-based routing quietly stops routing --
 * "spanish" on the queue, "Spanish" on the agent, and the work sits in a queue
 * that looks correctly configured. A shared list makes that unrepresentable.
 *
 * Laid out beside Teams because it is the same shape of problem: a thing, and
 * the set of people attached to it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  ClearRounded,
  DeleteOutline,
  PersonAddAlt1Rounded,
  SearchRounded,
  WorkspacePremiumRounded,
} from '@mui/icons-material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import { useCapability } from 'auth/usePermission';
import StyledAccessDenied from 'components/StyledAccessDenied';
import { getApiErrorMessage } from 'utils/apiErrors';
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const displayName = (u) =>
  `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.username || u.email;

const initials = (u) =>
  displayName(u).split(' ').filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase()).join('');

/** Codes are the stable identity a queue stores, so derive one from the name. */
const slugify = (name) =>
  (name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

export default function Skills() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();
  // Both hooks run unconditionally; `||` on the results, not on the calls.
  // Either capability, matching the queue admin router: every existing
  // telephony admin holds only the older one.
  const canManageQueues = useCapability('MANAGE_QUEUES');
  const canManageTelephony = useCapability('MANAGE_TELEPHONY');
  const canManage = canManageQueues || canManageTelephony;

  const [skills, setSkills] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [holders, setHolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', code: '', description: '' });
  // Whether the admin has hand-edited the code. Until they do it tracks the
  // name, because the code is an implementation detail they should not have to
  // think about -- but it is immutable once saved, so it must stay editable.
  const [codeTouched, setCodeTouched] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const [skillsRes, usersRes] = await Promise.all([
        axiosInstance.get('/call-center/skills'),
        axiosInstance.get('/users/'),
      ]);
      setSkills(skillsRes.data || []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.items ?? []);
      setSelectedId((current) => current ?? skillsRes.data?.[0]?.id ?? null);
    } catch (error) {
      setToast({ severity: 'error', message: getApiErrorMessage(error, 'Could not load skills.') });
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { load(); }, [load]);

  const loadHolders = useCallback(async (skillId) => {
    if (!skillId) { setHolders([]); return; }
    try {
      const { data } = await axiosInstance.get(`/call-center/skills/${skillId}/users`);
      setHolders(data || []);
    } catch {
      setHolders([]);
    }
  }, [axiosInstance]);

  useEffect(() => { loadHolders(selectedId); }, [selectedId, loadHolders]);

  const selected = useMemo(
    () => skills.find((s) => s.id === selectedId) ?? null,
    [skills, selectedId]
  );

  const visibleSkills = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter((s) =>
      `${s.name} ${s.code} ${s.description ?? ''}`.toLowerCase().includes(q)
    );
  }, [skills, search]);

  const holderIds = useMemo(() => new Set(holders.map((h) => h.id)), [holders]);

  const setUserSkills = async (userId, skillIds) => {
    await axiosInstance.put(`/call-center/users/${userId}/skills`, { skill_ids: skillIds });
  };

  const currentSkillIds = async (userId) => {
    const { data } = await axiosInstance.get(`/call-center/users/${userId}/skills`);
    return (data || []).map((s) => s.id);
  };

  const addHolder = async (user) => {
    if (!user || !selectedId) return;
    try {
      const existing = await currentSkillIds(user.id);
      await setUserSkills(user.id, [...new Set([...existing, selectedId])]);
      await load();
      await loadHolders(selectedId);
    } catch (error) {
      setToast({ severity: 'error', message: getApiErrorMessage(error, 'Could not add that person.') });
    }
  };

  const removeHolder = async (userId) => {
    try {
      const existing = await currentSkillIds(userId);
      await setUserSkills(userId, existing.filter((id) => id !== selectedId));
      await load();
      await loadHolders(selectedId);
    } catch (error) {
      setToast({ severity: 'error', message: getApiErrorMessage(error, 'Could not remove that person.') });
    }
  };

  const createSkill = async () => {
    const code = (codeTouched ? draft.code : slugify(draft.name)).trim();
    if (!draft.name.trim() || !code) return;
    try {
      const { data } = await axiosInstance.post('/call-center/skills', {
        code,
        name: draft.name.trim(),
        description: draft.description.trim() || null,
      });
      setCreating(false);
      setDraft({ name: '', code: '', description: '' });
      setCodeTouched(false);
      await load();
      setSelectedId(data.id);
    } catch (error) {
      setToast({ severity: 'error', message: getApiErrorMessage(error, 'Could not create the skill.') });
    }
  };

  const toggleActive = async (skill) => {
    try {
      await axiosInstance.put(`/call-center/skills/${skill.id}`, { is_active: !skill.is_active });
      await load();
    } catch (error) {
      setToast({ severity: 'error', message: getApiErrorMessage(error, 'Could not update the skill.') });
    }
  };

  const deleteSkill = async (skill) => {
    try {
      const { data } = await axiosInstance.delete(`/call-center/skills/${skill.id}`);
      setToast({ severity: 'success', message: data?.detail || 'Skill removed.' });
      setSelectedId(null);
      await load();
    } catch (error) {
      setToast({ severity: 'error', message: getApiErrorMessage(error, 'Could not remove the skill.') });
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <StyledAccessDenied />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isV2 ? (
          <PageHeader
            title="Skills"
            summary="A queue set to match on skills reaches anyone holding at least one of the skills it asks for."
            actions={(
              <MDButton variant="gradient" color="info" startIcon={<AddRounded />} onClick={() => setCreating(true)}>
                New skill
              </MDButton>
            )}
          />
        ) : (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Skills</Typography>
            <Typography variant="body2" color="text.secondary">
              A queue set to match on skills reaches anyone holding at least one of the skills it
              asks for.
            </Typography>
          </Box>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<AddRounded />}
            onClick={() => setCreating(true)}
          >
            New skill
          </MDButton>
        </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : !skills.length ? (
          <Box sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '12px' }}>
            <WorkspacePremiumRounded sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography variant="h6" gutterBottom>No skills yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Skills are optional. Queues route on capacity and idle time without them.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" gap={2.5} alignItems="flex-start" flexWrap="wrap">
            {/* ------------------------------------------------ skill list */}
            <Box sx={{ width: 320, flexShrink: 0, display: 'grid', gap: 1 }}>
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search skills"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                        <ClearRounded sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {!visibleSkills.length && (
                <Box sx={{ px: 1.5, py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No skills match &ldquo;{search}&rdquo;.
                  </Typography>
                </Box>
              )}

              {visibleSkills.map((skill) => {
                const active = skill.id === selectedId;
                return (
                  <Box
                    key={skill.id}
                    component="button"
                    type="button"
                    onClick={() => setSelectedId(skill.id)}
                    sx={{
                      font: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1.5,
                      py: 1.25,
                      border: '1px solid',
                      borderColor: active ? 'info.main' : 'divider',
                      borderRadius: '10px',
                      backgroundColor: active ? 'rgba(21,101,200,0.08)' : 'background.paper',
                      opacity: skill.is_active ? 1 : 0.55,
                      transition: 'border-color .15s, background-color .15s',
                      '&:hover': { borderColor: 'info.main' },
                    }}
                  >
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={600} noWrap>{skill.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {skill.user_count} {skill.user_count === 1 ? 'person' : 'people'}
                        {skill.is_active ? '' : ' · inactive'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* ---------------------------------------------- skill detail */}
            <Box
              sx={{
                flex: 1, minWidth: 420,
                border: '1px solid', borderColor: 'divider',
                borderRadius: '12px', backgroundColor: 'background.paper',
                p: 2.5,
              }}
            >
              {selected ? (
                <>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={2}>
                    <Box minWidth={0}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" fontWeight={700}>{selected.name}</Typography>
                        <Chip
                          size="small"
                          label={selected.code}
                          sx={{ height: 20, fontSize: 10.5, fontWeight: 600 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {selected.description || 'No description'}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Tooltip title={selected.is_active ? 'Queues can ask for this' : 'Hidden from queue setup'}>
                        <Switch
                          size="small"
                          checked={selected.is_active}
                          onChange={() => toggleActive(selected)}
                        />
                      </Tooltip>
                      <Tooltip title="Remove this skill">
                        <span>
                          <IconButton size="small" onClick={() => deleteSkill(selected)}>
                            <DeleteOutline sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>

                  {!selected.is_active && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Inactive. Queues cannot be set to require it, and it grants nobody
                      eligibility, but the people below keep it if you switch it back on.
                    </Alert>
                  )}

                  <Autocomplete
                    size="small"
                    options={users.filter((u) => !holderIds.has(u.id))}
                    getOptionLabel={displayName}
                    onChange={(_e, value) => addHolder(value)}
                    value={null}
                    blurOnSelect
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Give this skill to someone" />
                    )}
                    sx={{ maxWidth: 380, mb: 2 }}
                  />

                  <Box display="grid" gap={0.5}>
                    {holders.map((u) => (
                      <Box
                        key={u.id}
                        display="flex" alignItems="center" gap={1.5}
                        sx={{
                          px: 1.5, py: 1,
                          border: '1px solid', borderColor: 'divider', borderRadius: '10px',
                        }}
                      >
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700 }}>
                          {initials(u)}
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {displayName(u)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {u.username}
                          </Typography>
                        </Box>
                        <Tooltip title="Take this skill away">
                          <span>
                            <IconButton size="small" onClick={() => removeHolder(u.id)}>
                              <DeleteOutline sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ))}
                    {!holders.length && (
                      <Box textAlign="center" py={5}>
                        <PersonAddAlt1Rounded sx={{ fontSize: 34, opacity: 0.25 }} />
                        <Typography variant="body2" color="text.secondary" mt={1}>
                          Nobody has this skill yet. A queue requiring it will reach no one.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a skill to see who holds it.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </MDBox>

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New skill</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="Name"
            placeholder="e.g. Spanish"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth size="small" label="Code"
            value={codeTouched ? draft.code : slugify(draft.name)}
            onChange={(e) => {
              setCodeTouched(true);
              setDraft({ ...draft, code: e.target.value });
            }}
            helperText="Queues store this. It cannot be changed later."
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth size="small" label="Description" multiline rows={2}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={() => setCreating(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            disabled={!draft.name.trim()}
            onClick={createSkill}
          >
            Create
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={toast?.severity || 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
